import { supabase } from '../auth/supabaseClient';
import { apiBaseUrl } from './orgRuntime';

// Dynamic org ID set at runtime by AuthContext after enrollment resolves.
let _resolvedOrgId: string | null = null;

export function setResolvedOrgId(orgId: string | null) {
  _resolvedOrgId = orgId;
}

function getOrgId(): string | undefined {
  return _resolvedOrgId || undefined;
}

/**
 * Makes an authenticated API request to the backend.
 * Automatically includes:
 *   - Authorization: Bearer <jwt> (from Supabase session)
 *   - x-org-id (from resolved org context)
 *   - Content-Type: application/json
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  if (!apiBaseUrl) {
    throw new Error('Missing API configuration: VITE_API_BASE_URL.');
  }

  // Get the current session token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  // Build headers — only set Content-Type when there's a body, otherwise
  // Fastify rejects empty-body requests that claim application/json.
  const headers: Record<string, string> = {};

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  // Add auth token if available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add org id if configured
  const orgId = getOrgId();
  if (orgId) {
    headers['x-org-id'] = orgId;
  }

  // Merge any caller-provided headers (they take precedence)
  if (options.headers) {
    const extra = options.headers instanceof Headers
      ? Object.fromEntries((options.headers as Headers).entries())
      : Array.isArray(options.headers)
        ? Object.fromEntries(options.headers)
        : (options.headers as Record<string, string>);
    Object.assign(headers, extra);
  }

  // Make the request
  const response = await fetch(`${apiBaseUrl}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle errors
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  // Return parsed JSON
  return response.json();
}

/**
 * Convenience methods for common HTTP verbs.
 * All methods automatically include auth + org-id headers.
 */
export const api = {
  get: <T = any>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T = any>(endpoint: string, body?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T = any>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};
